import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const COLORS = ['#6B8E23', '#228B22', '#32CD32', '#90EE90', '#FFD700', '#FF8C00', '#FF6347', '#4682B4', '#9370DB', '#20B2AA'];
const ARCA_GREEN = '#6B8E23';

const DynamicSurveyAnalytics = ({ surveyId, surveyTitle, onBack }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ startDate: '', endDate: '', search: '' });
    const [activeTab, setActiveTab] = useState('questions');

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchAnalytics({});
    }, [surveyId]);

    const fetchAnalytics = async (appliedFilters = {}) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            Object.entries(appliedFilters).forEach(([key, value]) => { if (value) params.append(key, value); });
            const query = params.toString() ? `?${params.toString()}` : '';
            const res = await axios.get(`${API_URL}/survey/manage/${surveyId}/analytics${query}`, { headers });
            if (res.data.success) setAnalytics(res.data.data);
        } catch (err) {
            toast.error('Failed to load survey analytics');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => fetchAnalytics(filters);
    const clearFilters = () => {
        const cleared = { startDate: '', endDate: '', search: '' };
        setFilters(cleared);
        fetchAnalytics(cleared);
    };

    const hasActiveFilters = useMemo(() => Object.values(filters).some(v => v), [filters]);

    const exportChart = (chartId) => {
        const svg = document.querySelector(`#${chartId} svg`);
        if (!svg) return;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
            canvas.width = img.width * 2;
            canvas.height = img.height * 2;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const link = document.createElement('a');
            link.download = `${chartId}-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
    };

    const exportCSV = () => {
        if (!analytics) return;
        const rows = [];
        rows.push(['Survey', surveyTitle || analytics.survey.title]);
        rows.push(['Total Responses', analytics.total]);
        rows.push([]);
        rows.push(['Question', 'Type', 'Answer', 'Count', 'Percentage']);
        analytics.questions.forEach(q => {
            if (q.options && q.options.length) {
                q.options.forEach(o => rows.push([q.label, q.type, o.value, o.count, `${o.percentage}%`]));
            } else if (q.numeric && q.numeric.count > 0) {
                rows.push([q.label, q.type, `Avg: ${q.numeric.avg}, Min: ${q.numeric.min}, Max: ${q.numeric.max}`, q.numeric.count, '']);
            } else if (q.textResponses && q.textResponses.length) {
                q.textResponses.forEach(t => rows.push([q.label, q.type, t.value, '', '']));
            }
        });
        const csv = rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `survey-${surveyId}-analytics-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link); link.click(); link.remove();
        toast.success('Analytics CSV exported');
    };

    const metrics = useMemo(() => {
        if (!analytics) return [];
        const { total, timeline, questions } = analytics;
        const peak = timeline.length ? timeline.reduce((p, c) => c.count > p.count ? c : p, timeline[0]) : null;
        const avgCompletion = total > 0 && questions.length
            ? Math.round(questions.reduce((s, q) => s + (q.total / total), 0) / questions.length * 100)
            : 0;
        return [
            { label: 'Total Responses', value: total, icon: 'bi-people', color: ARCA_GREEN },
            { label: 'Questions', value: questions.length, icon: 'bi-list-check', color: '#4682B4' },
            { label: 'Peak Day', value: peak ? `${peak.date} (${peak.count})` : 'N/A', icon: 'bi-calendar-event', color: '#FF8C00' },
            { label: 'Avg Completion', value: `${avgCompletion}%`, icon: 'bi-check-circle', color: '#228B22' }
        ];
    }, [analytics]);

    const renderChart = (q) => {
        const chartId = `chart-q-${q.id}`;
        if (['select', 'radio'].includes(q.type)) {
            const data = q.options.map(o => ({ name: o.value, value: o.count }));
            const ChartComponent = q.options.length <= 5 ? (
                <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                        <Pie data={data} cx="50%" cy="50%" outerRadius={90} dataKey="value" label>
                            {data.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            ) : (
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill={ARCA_GREEN} radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            );
            return ChartComponent;
        }
        if (q.type === 'checkbox') {
            const data = q.options.slice(0, 10).map(o => ({ name: o.value, count: o.count }));
            return (
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={data} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#4682B4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            );
        }
        if (q.type === 'number') {
            if (q.distribution && q.distribution.length) {
                const data = q.distribution.map(d => ({ name: d.value, count: d.count }));
                return (
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill="#9370DB" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                );
            }
            return null;
        }
        return null;
    };

    if (loading && !analytics) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-success" role="status"></div>
                <p className="mt-3 text-muted">Loading analytics...</p>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="text-center py-5 text-muted">
                <i className="bi bi-bar-chart-line fs-1 d-block mb-3"></i>
                <p>Unable to load analytics.</p>
                <button className="btn btn-outline-secondary" onClick={onBack}><i className="bi bi-arrow-left me-1"></i>Back</button>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
                <div className="d-flex align-items-center">
                    <button className="btn btn-outline-secondary btn-sm me-3" onClick={onBack}><i className="bi bi-arrow-left"></i> Back</button>
                    <div>
                        <h4 className="mb-0 fw-bold">{surveyTitle || analytics.survey.title}</h4>
                        <small className="text-muted">Analytics — {analytics.total} total responses</small>
                    </div>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-success btn-sm" onClick={exportCSV}><i className="bi bi-download me-1"></i>Export CSV</button>
                </div>
            </div>

            {/* Metric Cards */}
            <div className="row g-3 mb-4">
                {metrics.map((m, idx) => (
                    <div key={idx} className="col-md-3 col-sm-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body d-flex align-items-center">
                                <div className="rounded-circle p-3 me-3" style={{ backgroundColor: `${m.color}15` }}>
                                    <i className={`bi ${m.icon} fs-4`} style={{ color: m.color }}></i>
                                </div>
                                <div>
                                    <h5 className="mb-0 fw-bold" style={{ color: m.color }}>{m.value}</h5>
                                    <small className="text-muted">{m.label}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3 col-sm-6">
                            <label className="form-label small text-muted mb-1">Start Date</label>
                            <input type="date" className="form-control form-control-sm" value={filters.startDate} onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))} />
                        </div>
                        <div className="col-md-3 col-sm-6">
                            <label className="form-label small text-muted mb-1">End Date</label>
                            <input type="date" className="form-control form-control-sm" value={filters.endDate} onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))} />
                        </div>
                        <div className="col-md-4 col-sm-8">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <input type="text" className="form-control form-control-sm" placeholder="Keyword in answers or email..." value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
                        </div>
                        <div className="col-md-2 col-sm-4 d-flex gap-2">
                            <button className="btn btn-success btn-sm" onClick={applyFilters} disabled={loading}>{loading ? '...' : 'Apply'}</button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters} disabled={loading}>Clear</button>
                        </div>
                        {hasActiveFilters && (
                            <div className="col-12">
                                <span className="badge bg-warning text-dark"><i className="bi bi-funnel-fill me-1"></i>Filtered view</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <ul className="nav nav-pills mb-4">
                {[
                    { id: 'questions', label: 'Question Breakdown', icon: 'bi-bar-chart' },
                    { id: 'insights', label: 'Insights', icon: 'bi-lightbulb' }
                ].map(tab => (
                    <li key={tab.id} className="nav-item">
                        <button className={`nav-link ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                            <i className={`bi ${tab.icon} me-1`}></i>{tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {activeTab === 'questions' && (
                <>
                    {/* Timeline */}
                    {analytics.timeline.length > 0 && (
                        <div className="card border-0 shadow-sm mb-4" id="chart-timeline">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Responses Over Time</h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart('chart-timeline')}><i className="bi bi-image"></i></button>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={analytics.timeline}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="count" stroke={ARCA_GREEN} strokeWidth={2} dot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Question Cards */}
                    <div className="row g-4">
                        {analytics.questions.map((q) => {
                            const chartId = `chart-q-${q.id}`;
                            const chart = renderChart(q);
                            return (
                                <div key={q.id} className="col-lg-6">
                                    <div className="card border-0 shadow-sm h-100" id={chartId}>
                                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="mb-0 fw-bold">{q.label}</h6>
                                                <small className="text-muted text-capitalize">{q.type} • {q.total} responses</small>
                                            </div>
                                            {chart && <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart(chartId)}><i className="bi bi-image"></i></button>}
                                        </div>
                                        <div className="card-body">
                                            {chart ? (
                                                chart
                                            ) : q.type === 'number' ? (
                                                <div className="text-center py-4">
                                                    {q.numeric.count > 0 ? (
                                                        <>
                                                            <h4 className="fw-bold text-success">{q.numeric.avg}</h4>
                                                            <p className="text-muted mb-1">Average</p>
                                                            <small className="text-muted">Min: {q.numeric.min} • Max: {q.numeric.max} • Count: {q.numeric.count}</small>
                                                        </>
                                                    ) : <p className="text-muted mb-0">No numeric responses</p>}
                                                </div>
                                            ) : (
                                                <div className="table-responsive" style={{ maxHeight: 280 }}>
                                                    <table className="table table-sm table-hover">
                                                        <thead className="table-light"><tr><th>Response</th><th>Date</th></tr></thead>
                                                        <tbody>
                                                            {q.textResponses.slice(0, 20).map((t, i) => (
                                                                <tr key={i}>
                                                                    <td className="small" style={{ whiteSpace: 'normal' }}>{t.value}</td>
                                                                    <td className="small text-nowrap">{t.submitted_at ? new Date(t.submitted_at).toLocaleDateString() : '-'}</td>
                                                                </tr>
                                                            ))}
                                                            {q.textResponses.length === 0 && <tr><td colSpan="2" className="text-muted">No text responses</td></tr>}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {activeTab === 'insights' && (
                <div className="card border-0 shadow-sm">
                    <div className="card-header bg-white d-flex align-items-center">
                        <i className="bi bi-lightbulb text-warning me-2"></i>
                        <h6 className="mb-0 fw-bold">Key Findings</h6>
                    </div>
                    <div className="card-body">
                        {analytics.insights.length === 0 ? (
                            <p className="text-muted mb-0">No insights yet. Collect more responses to see AI-style observations.</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {analytics.insights.map((insight, idx) => (
                                    <li key={idx} className="list-group-item ps-0">
                                        <i className="bi bi-check-circle-fill text-success me-2"></i>{insight}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DynamicSurveyAnalytics;
